import React from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import Cookies from 'js-cookie';
import { useLocation, useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CalendarDays,
  Check,
  ChevronDown,
  FileText,
  ImageUp,
  LogOut,
  Mail,
  MessagesSquare,
  ShieldCheck,
  Upload,
  UserRound,
} from 'lucide-react';

import {
  AddStartup,
  FetchAllCities,
  FetchAllCountries,
  FetchAllCountriesWithPhoneCodes,
  FetchAllStages,
  FetchAllStates,
} from '../../../api';
import { CancelDomainJoinRequest, FetchDomainStartups, FetchStartupDetailsByID, RequestDomainJoin } from '../../../api/profile';
import DomainStartupPrompt, { canCreateProfile } from './DomainStartupPrompt';
import LocationFields from './LocationFields';
import { useToast } from 'context/ToastContext';
import { checkForDomain, clearStateCookies, websiteTestURL } from 'utilities/index';
import { MixPanelOnboardingV2Tracker } from 'utilities/mixpanel';
import { STAGE_META, findStageMeta } from 'utilities/stageMeta';
import OptionChipGroup from 'ui-components/OptionChip';
import { Primary, Secondary } from 'ui-components/Buttons';
import LogoutConfirmationDialog from 'ui-components/popups/DeleteConfirmationPopup';
import BackrrLogo from '../../../assets/backrr_logo.png';
import LogoPlaceholder from '../../../assets/logo_placeholder.svg';
import CustomLogoButton from '../../../ui-components/formik/CustomLogoButton';
import {
  BORDER,
  BORDER_HOVER,
  FieldLabel,
  ICON_COLOR,
  INDIGO,
  TEXT_COLOR,
  TextInput,
  fieldSx,
  flagFor,
} from './fields';

// Persist a cookie so it survives the post-onboarding hard reload in every env.
// checkForDomain() returns a leading-dot domain (".backrr.com") only for the
// shared, registrable domains; for bare hosts (localhost, IPs, preview URLs) the
// browser rejects a cookie carrying that host as its `domain` attribute and drops
// it silently. In that case we set it host-only so it actually persists.
const setSharedCookie = (name, value) => {
  const domain = checkForDomain();
  if (typeof domain === 'string' && domain.startsWith('.')) {
    Cookies.set(name, value, { domain });
  } else {
    Cookies.set(name, value);
  }
};

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: { xs: 1.5, sm: 2, md: 2.5 },
      mb: { xs: 2.5, sm: 3, md: 4 },
    }}
  >
    <Box
      sx={{
        width: { xs: 40, sm: 48, md: 56 },
        height: { xs: 40, sm: 48, md: 56 },
        flexShrink: 0,
        // borderRadius: '12px',
        // border: `1px solid ${BORDER}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: ICON_COLOR,
      }}
    >
      <Icon size={{ xs: 20, sm: 24, md: 28 }} strokeWidth={1.7} />
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography
        sx={{
          fontSize: { xs: '1rem', sm: '1.15rem', md: '1.35rem' },
          fontWeight: 700,
          color: TEXT_COLOR,
          lineHeight: 1.3,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' },
          color: '#667085',
          lineHeight: 1.4,
        }}
      >
        {subtitle}
      </Typography>
    </Box>
  </Box>
);

const RadioDot = ({ selected, size = 20 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      flexShrink: 0,
      border: `2px solid ${selected ? INDIGO : '#D0D5DD'}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {selected && (
      <Box
        sx={{
          width: size / 2,
          height: size / 2,
          borderRadius: '50%',
          background: INDIGO,
        }}
      />
    )}
  </Box>
);

const StepBadge = ({ index, active, done, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.85rem',
        fontWeight: 600,
        flexShrink: 0,
        color: active || done ? '#fff' : '#98A2B3',
        background: active || done ? INDIGO : 'transparent',
        border: active || done ? 'none' : `1.5px solid #D0D5DD`,
      }}
    >
      {done ? <Check size={16} /> : index}
    </Box>
    <Typography
      sx={{
        fontSize: { xs: '0.8rem', sm: '0.95rem' },
        fontWeight: active ? 600 : 500,
        color: active || done ? TEXT_COLOR : '#98A2B3',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
  </Box>
);

// ---------------------------------------------------------------------------
// Icon components
// ---------------------------------------------------------------------------
const Step1Icon = () => (
  <svg
    width="64"
    height="64"
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="0.5"
      y="0.5"
      width="63"
      height="63"
      rx="15.5"
      stroke="url(#paint0_linear_763_131)"
      strokeWidth={2}
    />
    <path
      d="M20 20.5C21.933 20.5 23.5 22.067 23.5 24C23.5 25.933 21.933 27.5 20 27.5C18.067 27.5 16.5 25.933 16.5 24C16.5 22.067 18.067 20.5 20 20.5Z"
      stroke="url(#paint1_linear_763_131)"
      strokeWidth={2}
    />
    <path
      d="M14 37C14 28 26 28 26 37"
      stroke="url(#paint2_linear_763_131)"
      strokeLinecap="round"
      strokeWidth={2}
    />
    <rect
      x="29.5"
      y="31.5"
      width="19"
      height="14"
      rx="4.5"
      stroke="url(#paint3_linear_763_131)"
      strokeWidth={2}
    />
    <circle
      cx="34.3"
      cy="38.3"
      r="1.3"
      fill="url(#paint4_linear_763_131)"
      strokeWidth={2}
    />
    <circle
      cx="39.3"
      cy="38.3"
      r="1.3"
      fill="url(#paint5_linear_763_131)"
      strokeWidth={2}
    />
    <circle
      cx="44.3"
      cy="38.3"
      r="1.3"
      fill="url(#paint6_linear_763_131)"
      strokeWidth={2}
    />
    <defs>
      <linearGradient
        id="paint0_linear_763_131"
        x1="32"
        y1="0"
        x2="32"
        y2="64"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_763_131"
        x1="20"
        y1="20"
        x2="20"
        y2="28"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint2_linear_763_131"
        x1="20"
        y1="30.25"
        x2="20"
        y2="37"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint3_linear_763_131"
        x1="39"
        y1="31"
        x2="39"
        y2="46"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint4_linear_763_131"
        x1="34.3"
        y1="37"
        x2="34.3"
        y2="39.6"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint5_linear_763_131"
        x1="39.3"
        y1="37"
        x2="39.3"
        y2="39.6"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint6_linear_763_131"
        x1="44.3"
        y1="37"
        x2="44.3"
        y2="39.6"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
    </defs>
  </svg>
);

const Step2Icon = () => (
  <svg
    width="64"
    height="64"
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="1"
      y="1"
      width="62"
      height="62"
      rx="15"
      stroke="url(#paint0_linear_770_131)"
      strokeWidth="2"
    />
    <rect
      x="21"
      y="17"
      width="21"
      height="31"
      rx="1"
      stroke="url(#paint1_linear_770_131)"
      strokeWidth="2"
    />
    <rect
      x="24"
      y="20"
      width="5"
      height="4"
      rx="1"
      fill="url(#paint2_linear_770_131)"
    />
    <rect
      x="24"
      y="27"
      width="5"
      height="4"
      rx="1"
      fill="url(#paint3_linear_770_131)"
    />
    <rect
      x="24"
      y="34"
      width="5"
      height="4"
      rx="1"
      fill="url(#paint4_linear_770_131)"
    />
    <rect
      x="34"
      y="20"
      width="5"
      height="4"
      rx="1"
      fill="url(#paint5_linear_770_131)"
    />
    <rect
      x="34"
      y="27"
      width="5"
      height="4"
      rx="1"
      fill="url(#paint6_linear_770_131)"
    />
    <rect
      x="34"
      y="34"
      width="5"
      height="4"
      rx="1"
      fill="url(#paint7_linear_770_131)"
    />
    <defs>
      <linearGradient
        id="paint0_linear_770_131"
        x1="-16"
        y1="16"
        x2="48"
        y2="80"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_770_131"
        x1="14.25"
        y1="24.25"
        x2="45.2104"
        y2="45.8285"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint2_linear_770_131"
        x1="22.75"
        y1="21"
        x2="26.6524"
        y2="25.878"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint3_linear_770_131"
        x1="22.75"
        y1="28"
        x2="26.6524"
        y2="32.878"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint4_linear_770_131"
        x1="22.75"
        y1="35"
        x2="26.6524"
        y2="39.878"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint5_linear_770_131"
        x1="32.75"
        y1="21"
        x2="36.6524"
        y2="25.878"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint6_linear_770_131"
        x1="32.75"
        y1="28"
        x2="36.6524"
        y2="32.878"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint7_linear_770_131"
        x1="32.75"
        y1="35"
        x2="36.6524"
        y2="39.878"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
    </defs>
  </svg>
);

const BuildingIcon = () => (
  <svg
    width="14"
    height="16"
    viewBox="0 0 14 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="0.75"
      y="0.75"
      width="12.5"
      height="14.5"
      rx="0.75"
      stroke="url(#paint0_linear_building)"
      strokeWidth="1.5"
    />
    <rect
      x="3.5"
      y="3.5"
      width="2"
      height="2"
      rx="0.5"
      fill="url(#paint1_linear_building)"
    />
    <rect
      x="8"
      y="3.5"
      width="2"
      height="2"
      rx="0.5"
      fill="url(#paint2_linear_building)"
    />
    <rect
      x="3.5"
      y="7.5"
      width="2"
      height="2"
      rx="0.5"
      fill="url(#paint3_linear_building)"
    />
    <rect
      x="8"
      y="7.5"
      width="2"
      height="2"
      rx="0.5"
      fill="url(#paint4_linear_building)"
    />
    <rect
      x="3.5"
      y="11.5"
      width="2"
      height="2"
      rx="0.5"
      fill="url(#paint5_linear_building)"
    />
    <rect
      x="8"
      y="11.5"
      width="2"
      height="2"
      rx="0.5"
      fill="url(#paint6_linear_building)"
    />
    <defs>
      <linearGradient
        id="paint0_linear_building"
        x1="-3.5"
        y1="4"
        x2="12.3584"
        y2="17.8761"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_building"
        x1="3"
        y1="4"
        x2="5"
        y2="6"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint2_linear_building"
        x1="7.5"
        y1="4"
        x2="9.5"
        y2="6"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint3_linear_building"
        x1="3"
        y1="8"
        x2="5"
        y2="10"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint4_linear_building"
        x1="7.5"
        y1="8"
        x2="9.5"
        y2="10"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint5_linear_building"
        x1="3"
        y1="12"
        x2="5"
        y2="14"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint6_linear_building"
        x1="7.5"
        y1="12"
        x2="9.5"
        y2="14"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
    </defs>
  </svg>
);

const LegalEntityIcon = () => (
  <svg
    width="14"
    height="18"
    viewBox="0 0 14 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0.75 0.75H8.75L12.75 4.75V16.75H0.75V0.75Z"
      stroke="url(#paint0_linear_entity)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.75 0.75V4.75H12.75"
      stroke="url(#paint1_linear_entity)"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <rect
      x="3.25"
      y="8.75"
      width="7"
      height="1.3"
      rx="0.5"
      fill="url(#paint2_linear_entity)"
    />
    <rect
      x="3.25"
      y="11.75"
      width="5"
      height="1.3"
      rx="0.5"
      fill="url(#paint3_linear_entity)"
    />
    <defs>
      <linearGradient
        id="paint0_linear_entity"
        x1="-2.25"
        y1="4.75"
        x2="13.11"
        y2="16.27"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_entity"
        x1="7.75"
        y1="1.75"
        x2="11.75"
        y2="5.75"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint2_linear_entity"
        x1="1.5"
        y1="9.075"
        x2="1.96676"
        y2="11.5883"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
      <linearGradient
        id="paint3_linear_entity"
        x1="2"
        y1="12.075"
        x2="2.6332"
        y2="14.5104"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7852F8" />
        <stop offset="1" stopColor="#84AEF6" />
      </linearGradient>
    </defs>
  </svg>
);

const DateIcon = ({ sx }) => (
  <svg
    width="15"
    height="17"
    viewBox="0 0 15 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ color: ICON_COLOR, ...sx }}
  >
    <rect
      x="0.75"
      y="3.75"
      width="13.5"
      height="12.5"
      rx="1.25"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <rect y="7" width="15" height="1.3" fill="currentColor" />
    <rect x="3.5" width="1.4" height="4" rx="0.7" fill="currentColor" />
    <rect x="10.1001" width="1.4" height="4" rx="0.7" fill="currentColor" />
  </svg>
);

// ---------------------------------------------------------------------------
// Step URLs
// ---------------------------------------------------------------------------
// Each onboarding step has its own address (startup.backrr.com/onboarding/step-1,
// .../step-2) so founders can see, bookmark and share where they are.
const ONBOARDING_STEP_PATHS = {
  1: '/onboarding/step-1',
  2: '/onboarding/step-2',
};
const stepPath = (step) =>
  ONBOARDING_STEP_PATHS[step] || ONBOARDING_STEP_PATHS[1];
const stepFromPathname = (pathname = '') => {
  const match = pathname.match(/\/onboarding\/step-([12])\/?$/);
  return match ? Number(match[1]) : null;
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const OnboardingV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  React.useEffect(() => {
    window.document.getElementsByTagName('body')[0].style.background =
      '#FFFDFA';
  }, []);

  // The URL is what decides which step renders. Step 2 is only reachable once
  // step 1 has created the startup, so without an id we fall back to step 1
  // rather than submitting step 2 with no startup_id.
  const [currentStep, setCurrentStep] = React.useState(() => {
    const requestedStep = stepFromPathname(location.pathname) || 1;
    return requestedStep >= 2 && Cookies.get('X-ST-ID') ? 2 : 1;
  });

  // The single way to move between steps: persist progress, update state and
  // push the step's URL so the address bar always matches what's rendered.
  const goToStep = React.useCallback(
    (step) => {
      Cookies.set('step', step);
      setCurrentStep(step);
      navigate(stepPath(step));
    },
    [navigate]
  );

  // The other direction — follow the URL when it changes on its own (browser
  // back/forward, or a bookmarked step).
  React.useEffect(() => {
    const pathStep = stepFromPathname(location.pathname);
    // No step in the URL, or step 2 before step 1 has created the startup —
    // either way the founder belongs on step 1.
    if (!pathStep || (pathStep === 2 && !Cookies.get('X-ST-ID'))) {
      navigate(stepPath(1), { replace: true });
      return;
    }
    if (pathStep !== currentStep) {
      setCurrentStep(pathStep);
      Cookies.set('step', pathStep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  const [startupId, setStartupId] = React.useState(
    Cookies.get('X-ST-ID') || null
  );

  const [openLogoutConfirmation, setOpenLogoutConfirmation] =
    React.useState(false);

  // Errors are held back until the founder tries to move on from a step; each
  // step starts clean.
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  React.useEffect(() => {
    setSubmitAttempted(false);
  }, [currentStep]);

  // Reset on every step change so Mixpanel's time_spent measures the step the
  // founder just finished, not the whole session.
  const stepStartTimeRef = React.useRef(new Date());
  React.useEffect(() => {
    stepStartTimeRef.current = new Date();
  }, [currentStep]);

  // Data
  const { data: phoneCodeCountries } = FetchAllCountriesWithPhoneCodes({});
  const { data: countries } = FetchAllCountries(
    {}
  );
  const { data: stages } = FetchAllStages();
  const { data: existingStartup, refetch: refetchStartup } =
    FetchStartupDetailsByID({
      startup_id: startupId,
    });

  const {
    mutateAsync: addStartupAsync,
    isLoading: isSubmitting,
    data: AddStartupResponse,
  } = AddStartup({ setProgress: () => {} });

  // Before a first profile exists, offer startups already on the user's company domain.
  const checkDomain = currentStep === 1 && !startupId;
  const {
    data: domainData,
    isLoading: isLoadingDomainStartups,
    refetch: refetchDomainStartups,
  } = FetchDomainStartups({ enabled: checkDomain });
  const domainStartups = domainData?.startups;
  const joinedStartupId = domainData?.joined_startup_id;
  const [createOwnProfile, setCreateOwnProfile] = React.useState(false);
  const [domainBusyId, setDomainBusyId] = React.useState(null);
  const { mutateAsync: requestDomainJoinAsync } = RequestDomainJoin();
  const { mutateAsync: cancelDomainJoinAsync } = CancelDomainJoinRequest();
  const showDomainPrompt =
    checkDomain &&
    domainStartups?.length > 0 &&
    (!createOwnProfile || !canCreateProfile(domainStartups));

  const runDomainAction = async (id, action) => {
    setDomainBusyId(id);
    try {
      const response = await action(id);
      addToast(response?.data?.message || 'Done', 'success');
    } catch (err) {
      addToast(err?.response?.data?.message || 'Something went wrong', 'error');
    } finally {
      setDomainBusyId(null);
      refetchDomainStartups();
    }
  };

  // The owner approved while this screen was open.
  // Hard reload so the app loads the joined startup and its permissions.
  React.useEffect(() => {
    if (!checkDomain || !joinedStartupId) return;
    setSharedCookie('X-ST-ID', joinedStartupId);
    document.cookie = `X-ST-ID=${joinedStartupId}; path=/`;
    window.location.href = '/onboarded';
  }, [checkDomain, joinedStartupId]);

  // Pull the latest saved startup data whenever the founder moves between steps
  // so each step renders server-fresh values (not stale in-session state).
  // React.useEffect(() => {
  //   if (startupId) refetchStartup();
  // }, [currentStep]);

  // React to each successful save (mirrors the old onboarding flow). react-query
  // resets `data` to undefined on every new mutate, so statusCode goes
  // 200 -> undefined -> 200 between steps and this effect re-fires per step.
  React.useEffect(() => {
    if (AddStartupResponse?.data?.statusCode !== 200) return;

    // Sync the saved startup id to state + cookie (host-only fallback so it
    // survives on localhost/HTTP, where a `domain` attribute is rejected).
    const savedId = AddStartupResponse?.data?.data?._id;
    if (savedId) {
      setStartupId(savedId);
      setSharedCookie('X-ST-ID', savedId);
      document.cookie = `X-ST-ID=${savedId}; path=/`;
    }

    if (currentStep === 1) {
      // Advance to step 2, persisting progress so a refresh resumes correctly.
      goToStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Refresh so step 2 renders server-fresh values.
      refetchStartup();
    } else {
      // Final step. routes/index.js reads is_profile_complete only at render
      // time, and writing a cookie is NOT reactive — so we must force a fresh
      // render of the router. A full reload does that.
      // We deliberately do NOT refetchStartup() here: its select overwrites
      // is_profile_complete with the server value and would clobber this write.
      navigate("/onboarded")
      // clearStateCookies();
      // setSharedCookie('is_profile_complete', true);
      // document.cookie = 'is_profile_complete=true; path=/';
      // Cookies.remove('step');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [AddStartupResponse?.data?.statusCode]);

  const validationObj =
    currentStep === 1
      ? {
          first_name: Yup.string().required('First name is required').trim(),
          last_name: Yup.string().required('Last name is required').trim(),
          email: Yup.string()
            .email('Please enter a valid email')
            .required('Email address is required')
            .trim(),
          phone_code: Yup.string().required('Phone code is required'),
          mobile_number: Yup.string()
            .matches(/^[0-9]+$/, 'Mobile number can only contain digits')
            .min(5, 'Mobile number must be at least 5 digits')
            .max(15, 'Mobile number cannot be more than 15 digits')
            .required('Mobile number is required'),
          // One Location field stands for all three ids, and a place is
          // stored at whatever precision it has: a city carries a state and
          // country, a state carries a country, a country carries only
          // itself. Discovery filters on each level independently, so a
          // coarser answer narrows which filters a startup appears in rather
          // than hiding it - which leaves country as the only one to demand.
          country_id: Yup.string().nullable().required('Location is required'),
        }
      : {
          startup_name: Yup.string()
            .required('Startup name is required')
            .trim(),
          website_url: Yup.string()
            .trim()
            .matches(websiteTestURL, 'Please enter a valid website URL'),
          stage_key: Yup.string().required('Please select a stage'),
          raised_round: Yup.boolean()
            .typeError('This field is required')
            .required('This field is required'),
        };

  const getStageKeyFromStageId = (stageId) => {
    if (!stageId || !stages) return '';
    const stage = stages.find((s) => s._id === stageId);
    if (!stage) return '';
    return findStageMeta(stage.name)?.key || '';
  };

  const formik = useFormik({
    initialValues: {
      first_name: existingStartup?.data?.first_name || '',
      last_name: existingStartup?.data?.last_name || '',
      email: existingStartup?.data?.email || Cookies.get('email') || '',
      phone_code: existingStartup?.data?.phone_code || '91',
      mobile_number: existingStartup?.data?.mobile_number || '',
      country_id: existingStartup?.data?.country_id || '',
      state_id: existingStartup?.data?.state_id || '',
      city_id: existingStartup?.data?.city_id || '',
      startup_logo: existingStartup?.data?.startup_logo || null,
      startup_name: existingStartup?.data?.startup_name || '',
      website_url: existingStartup?.data?.website_url || '',
      stage_key: getStageKeyFromStageId(existingStartup?.data?.stage_id),
      entity_name: existingStartup?.data?.entity_name || '',
      formation_date: existingStartup?.data?.formation_date || null,
      raised_round: existingStartup?.data?.raised_round || null,
    },
    validationSchema: Yup.object({ ...validationObj }),
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        if (startupId) formData.append('startup_id', startupId);
        formData.append('email', values.email || Cookies.get('email') || '');
        formData.append('profile_source', 'startup_profile');

        let resolvedStageId;

        if (currentStep === 1) {
          formData.append('first_name', values.first_name);
          formData.append('last_name', values.last_name);
          formData.append(
            'founder[name]',
            values.first_name + ' ' + values?.last_name
          );
          formData.append('mobile_number', values.mobile_number);
          formData.append('phone_code', values.phone_code);
          if (values.country_id)
            formData.append('country_id', values.country_id);
          if (values.state_id) formData.append('state_id', values.state_id);
          if (values.city_id) formData.append('city_id', values.city_id);
          formData.append('step', 2);
        } else {
          const resolvedStage = (stages || []).find(
            (s) => findStageMeta(s?.name)?.key === values.stage_key
          );
          resolvedStageId = resolvedStage?._id;

          formData.append('startup_name', values.startup_name);
          if (values.website_url)
            formData.append('website_url', values.website_url);
          if (resolvedStage?._id)
            formData.append('stage_id', resolvedStage._id);
          if (values.entity_name?.trim())
            formData.append('entity_name', values.entity_name.trim());
          if (values.formation_date)
            formData.append(
              'formation_date',
              moment(values.formation_date).toISOString()
            );
          formData.append('raised_round', values.raised_round);
          if (values.startup_logo && typeof values.startup_logo !== 'string') {
            formData.append('startup_logo', values.startup_logo);
          }
          formData.append('is_profile_completed', true);
        }

        // Fire the save; the AddStartupResponse effect handles step advance,
        // cookie/state sync, and the final redirect on success.
        const response = await addStartupAsync(formData);

        // Track only saves that actually landed. The id comes off the response
        // because the cookie is written later, by the AddStartupResponse effect.
        MixPanelOnboardingV2Tracker(
          currentStep,
          {
            ...values,
            stage_id: resolvedStageId,
            stepStartTime: stepStartTimeRef.current,
          },
          {
            startup_id: response?.data?.data?._id || startupId,
            countries,
            states,
            cities,
            stages,
          }
        );
      } catch (err) {
        // The domain's startup filled up, or a request is waiting: show the prompt again.
        if (['DOMAIN_STARTUP_FULL', 'DOMAIN_JOIN_PENDING'].includes(err?.response?.data?.errorCode)) {
          refetchDomainStartups();
        }
        addToast(
          err?.response?.data?.message || 'Something went wrong',
          'error'
        );
      }
    },
  });

  // Validation runs on every change, but errors stay hidden until the step is
  // submitted once. After that they surface, and clear again as soon as the
  // field is filled in correctly.
  const errorFor = (field) =>
    submitAttempted ? formik.errors[field] : undefined;

  const submitStep = (e) => {
    setSubmitAttempted(true);
    formik.handleSubmit(e);
  };

  // LocationFields owns the location inputs now. These lists stay because the
  // mixpanel tracker resolves the saved ids back into names with them; the
  // query keys match, so react-query serves both callers from one fetch.
  const selectedCountry = (countries || []).find(
    (c) => c._id === formik.values.country_id
  );
  const { data: states } = FetchAllStates({
    countryID: selectedCountry?.value,
  });
  const selectedState = (states || []).find(
    (s) => s._id === formik.values.state_id
  );
  const { data: cities } = FetchAllCities({
    stateID: selectedState?.value,
  });


  const selectedPhone =
    (phoneCodeCountries || []).find(
      (c) => String(c.code) === String(formik.values.phone_code)
    ) || null;

  const handleLogoUpdate = (file) => {
    formik.setFieldValue('startup_logo', file);
  };

  // Shared styling for the country/state autocompletes so they match the inputs
  // Mirrors fieldSx so the Autocomplete fields line up (height, icon offset,
  // text baseline) with the plain TextInput fields.

  return (
    <Box
      sx={{
        // minHeight: '100dvh',
        background: '#fffdfa',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top bar with Backrr logo */}
      <Box
        sx={{
          background: '#fffdfa',
          // borderBottom: `1px solid ${BORDER}`,
          px: { xs: 2.5, sm: 5 },
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <img
          src={BackrrLogo}
          alt="Backrr"
          style={{ height: '34px', width: 'auto', objectFit: 'contain' }}
        />
        <button
          onClick={() => {
            setOpenLogoutConfirmation(true);
          }}
          title="Logout"
          style={{
            background: 'transparent',
            border: 'none',
            color: '#667085',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s ease, color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#F3F4F6';
            e.currentTarget.style.color = '#475467';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#667085';
          }}
        >
          <LogOut size={20} strokeWidth={1.5} />
        </button>
      </Box>

      {/* Stepper */}
      <Box
        sx={{
          background: '#fffdfa',
          // borderBottom: `1px solid ${BORDER}`,
          py: 2.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 1.5, sm: 3 },
          position: 'sticky',
          top: '65px',
          zIndex: 9,
        }}
      >
        <StepBadge
          index={1}
          active={currentStep === 1}
          done={currentStep > 1}
          label="Contact Information"
        />
        <Box
          sx={{
            width: { xs: 32, sm: 120 },
            height: '2px',
            background: currentStep > 1 ? INDIGO : '#E4E7EC',
          }}
        />
        <StepBadge
          index={2}
          active={currentStep === 2}
          done={false}
          label="Startup Information"
        />
      </Box>

      {/* Form card */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          px: { xs: 1.5, sm: 3 },
          py: { xs: 2.5, sm: 4 },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 960,
            background: '#fff',

            border: '1px solid #EFEFF2',
            borderRadius: { xs: '16px', sm: '20px' },
            boxShadow:
              '0 1px 3px rgba(16,24,40,0.06), 0 1px 2px rgba(16,24,40,0.04)',
            p: { xs: 2.5, sm: 5 },
          }}
        >
          {checkDomain && isLoadingDomainStartups ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : showDomainPrompt ? (
            <DomainStartupPrompt
              startups={domainStartups}
              busyId={domainBusyId}
              requesterName={domainData?.requester_name}
              onRequest={(id, name) => runDomainAction(id, () => requestDomainJoinAsync({ startup_id: id, ...name }))}
              onCancel={(id) => runDomainAction(id, cancelDomainJoinAsync)}
              onCreateNew={() => setCreateOwnProfile(true)}
            />
          ) : (
          <FormikProvider value={formik}>
            <Form
              onSubmit={submitStep}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.preventDefault();
              }}
            >
              {currentStep === 1 ? (
                // ---------------------------- STEP 1 ----------------------------
                <>
                  <SectionHeader
                    icon={Step1Icon}
                    title="Contact Information"
                    subtitle="This information will be used to contact you."
                  />

                  <Grid
                    container
                    rowSpacing={{ xs: 1 }}
                    columnSpacing={{ xs: 1.8 }}
                  >
                    <Grid item xs={12} sm={6}>
                      <FieldLabel required>FIRST NAME</FieldLabel>
                      <TextInput
                        icon={UserRound}
                        placeholder="Enter first name"
                        value={formik.values.first_name}
                        error={errorFor('first_name')}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                          formik.setFieldValue(
                            'first_name',
                            v.charAt(0).toUpperCase() + v.slice(1)
                          );
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel required>LAST NAME</FieldLabel>
                      <TextInput
                        icon={UserRound}
                        placeholder="Enter last name"
                        value={formik.values.last_name}
                        error={errorFor('last_name')}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                          formik.setFieldValue(
                            'last_name',
                            v.charAt(0).toUpperCase() + v.slice(1)
                          );
                        }}
                      />
                    </Grid>

                    {/* <Grid item xs={12}>
                      <FieldLabel required>EMAIL ADDRESS</FieldLabel>
                      <TextInput
                        icon={Mail}
                        placeholder="Enter your email address"
                        value={formik.values.email}
                        error={errorFor('email')}
                        onChange={(e) =>
                          formik.setFieldValue('email', e.target.value.trim())
                        }
                      />
                    </Grid> */}

                    <Grid item xs={12}>
                      <FieldLabel required>MOBILE NUMBER</FieldLabel>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1.5,
                          alignItems: 'stretch',
                        }}
                      >
                        <Select
                          value={formik.values.phone_code}
                          onChange={(e) =>
                            formik.setFieldValue('phone_code', e.target.value)
                          }
                          variant="filled"
                          disableUnderline
                          renderValue={() => (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.8,
                              }}
                            >
                              {flagFor(selectedPhone) ? (
                                <img
                                  src={flagFor(selectedPhone)}
                                  alt={selectedPhone?.label || 'flag'}
                                  width={24}
                                  height={16}
                                  style={{
                                    width: 24,
                                    height: 16,
                                    objectFit: 'cover',
                                    borderRadius: 2,
                                    display: 'block',
                                  }}
                                />
                              ) : (
                                <span
                                  style={{ fontSize: '1.1rem', lineHeight: 1 }}
                                >
                                  {selectedPhone?.emoji || '🌐'}
                                </span>
                              )}
                              <span
                                style={{ fontWeight: 600, color: TEXT_COLOR }}
                              >
                                +{formik.values.phone_code}
                              </span>
                            </Box>
                          )}
                          IconComponent={(p) => (
                            <ChevronDown {...p} size={18} color="#667085" />
                          )}
                          MenuProps={{
                            PaperProps: { style: { maxHeight: 320 } },
                          }}
                          sx={{
                            minWidth: 110,
                            height: '100%',
                            borderRadius: '12px',
                            background: '#fff',
                            border: `1px solid ${BORDER}`,
                            '& .MuiSelect-select': {
                              display: 'flex',
                              alignItems: 'center',
                              padding: '15px 12px !important',
                              height: '100%',
                            },
                            '&:hover': { borderColor: BORDER_HOVER },
                            '&.Mui-focused': {
                              borderColor: ICON_COLOR,
                              boxShadow: '0 0 0 4px rgba(99,102,241,0.12)',
                            },
                          }}
                        >
                          {(phoneCodeCountries || []).map((c, i) => (
                            <MenuItem
                              key={`${c._id}-${i}`}
                              value={String(c.code)}
                            >
                              {flagFor(c) ? (
                                <img
                                  src={flagFor(c)}
                                  alt={c.label || 'flag'}
                                  width={24}
                                  height={16}
                                  style={{
                                    width: 24,
                                    height: 16,
                                    objectFit: 'cover',
                                    borderRadius: 2,
                                    marginRight: 8,
                                    display: 'inline-block',
                                    verticalAlign: 'middle',
                                  }}
                                />
                              ) : (
                                <span style={{ marginRight: 8 }}>
                                  {c.emoji || '🌐'}
                                </span>
                              )}
                              {c.label} (+{c.code})
                            </MenuItem>
                          ))}
                        </Select>
                        <Box sx={{ flex: 1 }}>
                          <TextInput
                            placeholder="Enter your mobile number"
                            inputProps={{ inputMode: 'numeric' }}
                            value={formik.values.mobile_number}
                            error={errorFor('mobile_number')}
                            onChange={(e) =>
                              formik.setFieldValue(
                                'mobile_number',
                                e.target.value.replace(/[^0-9]/g, '')
                              )
                            }
                          />
                        </Box>
                      </Box>
                    </Grid>
                    <LocationFields formik={formik} errorFor={errorFor} />
                  </Grid>

                  {/* Footer */}
                  <Box
                    sx={{
                      mt: { xs: 1, sm: 1 },
                      pt: 3,
                      borderTop: `1px solid ${BORDER}`,
                      display: 'flex',
                      flexDirection: { xs: 'column-reverse', sm: 'row' },
                      gap: 2,
                      alignItems: { xs: 'stretch', sm: 'center' },
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: '#667085',
                      }}
                    >
                      {/* <ShieldCheck size={18} color={ICON_COLOR} />
                      <Typography
                        sx={{ fontSize: '0.85rem', color: '#475467' }}
                      >
                        We'll never share your contact details with anyone.
                      </Typography> */}
                    </Box>
                    <NextButton
                      loading={isSubmitting}
                      label="Next"
                      onClick={submitStep}
                    />
                  </Box>
                </>
              ) : (
                // ---------------------------- STEP 2 ----------------------------
                <>
                  <SectionHeader
                    icon={Step2Icon}
                    title="Startup Information"
                    subtitle="Provide the following information for your startup"
                  />

                  {/* Logo upload with cropper */}
                  <Box sx={{ mb: 2.8 }}>
                    <FieldLabel optional>Logo</FieldLabel>
                    <CustomLogoButton
                      label=""
                      name="startup_logo"
                      value={formik.values.startup_logo}
                      handleUpdateFile={handleLogoUpdate}
                      Pwidth={72}
                      Pheight={72}
                      placeholder={LogoPlaceholder}
                    />
                  </Box>

                  <Grid
                    container
                    rowSpacing={{ xs: 0 }}
                    columnSpacing={{ xs: 1.8 }}
                    sx={{ mb: 1 }}
                  >
                    <Grid item xs={12} sm={6}>
                      <FieldLabel required>Startup Name</FieldLabel>
                      <TextInput
                        icon={BuildingIcon}
                        // placeholder="Enter startup name"
                        // value={formik.values.startup_name}
                        // error={errorFor('startup_name')}
                        // onChange={(e) =>
                        //   formik.setFieldValue('startup_name', e.target.value)
                        // }
                        name="startup_name"
                    pattern="^[^@$()]*$"  // This regex pattern excludes @, $, (, and )
                    placeholder="Enter startup name"
                    value={formik.values?.startup_name}
                    error={errorFor('startup_name')}
                    key="startup_name"
                    onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= 30 && /^[^@$()]*$/.test(value)) {
                            formik.setFieldValue(
                                "startup_name",
                                value.charAt(0).toUpperCase() + value.slice(1)
                            );
                        }
                    }}
                    fullWidth
                      />
                     
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel optional>Website URL</FieldLabel>
                      <TextInput
                        icon={() => (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle
                              cx="8"
                              cy="8"
                              r="7.25"
                              stroke="url(#paint0_linear_8_333)"
                              stroke-width="1.5"
                            />
                            <path
                              d="M8 0.599609C8.75487 0.599609 9.60003 1.22742 10.292 2.61133C10.966 3.95937 11.4004 5.86381 11.4004 8C11.4004 10.1362 10.966 12.0406 10.292 13.3887C9.60003 14.7726 8.75487 15.4004 8 15.4004C7.24513 15.4004 6.39997 14.7726 5.70801 13.3887C5.03399 12.0406 4.59961 10.1362 4.59961 8C4.59961 5.86381 5.03399 3.95937 5.70801 2.61133C6.39997 1.22742 7.24513 0.599609 8 0.599609Z"
                              stroke="url(#paint1_linear_8_333)"
                              stroke-width="1.2"
                            />
                            <path
                              d="M0 8H16"
                              stroke="url(#paint2_linear_8_333)"
                              stroke-width="1.2"
                            />
                            <defs>
                              <linearGradient
                                id="paint0_linear_8_333"
                                x1="-4"
                                y1="4"
                                x2="12"
                                y2="20"
                                gradientUnits="userSpaceOnUse"
                              >
                                <stop stop-color="#7852F8" />
                                <stop offset="1" stop-color="#84AEF6" />
                              </linearGradient>
                              <linearGradient
                                id="paint1_linear_8_333"
                                x1="2"
                                y1="4"
                                x2="14.8"
                                y2="10.4"
                                gradientUnits="userSpaceOnUse"
                              >
                                <stop stop-color="#7852F8" />
                                <stop offset="1" stop-color="#84AEF6" />
                              </linearGradient>
                              <linearGradient
                                id="paint2_linear_8_333"
                                x1="-4"
                                y1="8.25"
                                x2="-3.87549"
                                y2="10.2422"
                                gradientUnits="userSpaceOnUse"
                              >
                                <stop stop-color="#7852F8" />
                                <stop offset="1" stop-color="#84AEF6" />
                              </linearGradient>
                            </defs>
                          </svg>
                        )}
                        prefix="https://"
                        placeholder="www.example.com"
                        value={formik.values.website_url}
                        error={errorFor('website_url')}
                        onChange={(e) =>
                          formik.setFieldValue("website_url", String(e.target.value)?.startsWith("https://") ? String(e.target.value)?.replace("https://", "") : e.target.value)
                        }
                        extraSx={{
                          '& .MuiInputBase-root': {
                            paddingInline: '0.5rem',
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* Stage selection */}
                  <Box sx={{ mb: 2.8 }}>
                    <FieldLabel required capitalize={false}>
                      What is the stage of your startup?
                    </FieldLabel>
                    <OptionChipGroup
                      options={STAGE_META}
                      value={formik.values.stage_key}
                      onChange={(value) =>
                        formik.setFieldValue('stage_key', value)
                      }
                    />
                    {errorFor('stage_key') && (
                      <Typography
                        sx={{ color: '#F04438', fontSize: '0.75rem', mt: 1 }}
                      >
                        {errorFor('stage_key')}
                      </Typography>
                    )}
                  </Box>

                  <Grid
                    container
                    rowSpacing={{ xs: 0 }}
                    columnSpacing={{ xs: 1.8 }}
                    sx={{ mb: 1 }}
                  >
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Legal Entity Name</FieldLabel>
                      <TextInput
                        icon={LegalEntityIcon}
                        placeholder="Enter legal entity name"
                        value={formik.values.entity_name}
                        onChange={(e) =>
                          formik.setFieldValue('entity_name', e.target.value?.charAt(0)?.toUpperCase() + e.target.value?.slice(1))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Formation Date</FieldLabel>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                          format="MM/DD/YYYY"
                          value={
                            formik.values.formation_date
                              ? moment(formik.values.formation_date)
                              : null
                          }
                          onChange={(v) =>
                            formik.setFieldValue(
                              'formation_date',
                              v ? v.toISOString() : null
                            )
                          }
                          maxDate={moment()}
                          slots={{
                            openPickerIcon: DateIcon,
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              variant: 'filled',
                              placeholder: 'MM/DD/YYYY',
                              InputProps: { disableUnderline: true },
                              sx: fieldSx,
                            },
                            inputAdornment: { position: 'start' },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>

                  {/* Raised funds */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: { xs: 2, sm: 4 },
                    }}
                  >
                    <FieldLabel required>
                      Have you raised funds earlier?
                    </FieldLabel>
                    <Box sx={{ display: 'flex', gap: 4, mb: '8px' }}>
                      {[
                        { label: 'Yes', value: true },
                        { label: 'No', value: false },
                      ].map((opt) => (
                        <Box
                          key={opt.label}
                          onClick={() =>
                            formik.setFieldValue('raised_round', opt.value)
                          }
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'pointer',
                          }}
                        >
                          <RadioDot
                            selected={formik.values.raised_round === opt.value}
                          />
                          <Typography sx={{ color: TEXT_COLOR }}>
                            {opt.label}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  {errorFor('raised_round') && (
                    <Typography
                      sx={{ color: '#F04438', fontSize: '0.75rem', mt: 0.5 }}
                    >
                      {errorFor('raised_round')}
                    </Typography>
                  )}

                  {/* Footer */}
                  <Box
                    sx={{
                      mt: { xs: 2, sm: 2 },
                      pt: 3,
                      borderTop: `1px solid ${BORDER}`,
                      display: 'flex',
                      gap: 2,
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexDirection: { xs: 'row', sm: 'row' },
                    }}
                  >
                    <Secondary
                      onClick={() => {
                        goToStep(1);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      startIcon={<ArrowLeft size={18} />}
                      sx={{
                        px: { xs: 2, sm: 4 },
                        py: 1.6,
                        minWidth: { xs: '100px', sm: '160px' },
                        flex: { xs: 1, sm: '0 0 auto' },
                      }}
                    >
                      Back
                    </Secondary>
                    <NextButton
                      loading={isSubmitting}
                      label="Next"
                      onClick={submitStep}
                    />
                  </Box>
                </>
              )}
            </Form>
          </FormikProvider>
          )}
        </Box>
      </Box>

      <LogoutConfirmationDialog
        open={openLogoutConfirmation}
        handleClose={() => setOpenLogoutConfirmation(false)}
        handleSubmission={() => {
          setOpenLogoutConfirmation(false);
          navigate('/logout');
        }}
        heading="Confirm Logout"
        subheading="Are you sure you want to log out of your account?"
        ActionText="Logout"
      />
    </Box>
  );
};

const NextButton = ({ loading, label, onClick }) => (
  <Primary
    onClick={loading ? undefined : onClick}
    disabled={loading}
    sx={{
      px: { xs: 2, sm: 4 },
      py: 1.6,
      minWidth: { xs: '100px', sm: '160px' },
      flex: { xs: 1, sm: '0 0 auto' },
    }}
  >
    {loading ? (
      <CircularProgress size={20} sx={{ color: '#fff' }} />
    ) : (
      <Box
        component="span"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        {label} <ArrowRight size={18} />
      </Box>
    )}
  </Primary>
);

export default OnboardingV2;
